import { pick } from 'lodash-es';
import { useContext, useEffect, useRef } from 'react';
import { Context } from './context.js';
import { startAgent, retireRoomStarts } from './start-signal.js';
import {
  fieldKeyedAgentsEqual,
  idleRestartDelayMs,
  signalAgentPick
} from './use-agents-field-eq.js';
import { useAgents } from '#~/agent/hooks.js';
import { depot } from '#~/depot/index.js';
import type { Agent } from '@studio/models/agent';

/** `<Signal>` component. */
export const Signal = (): null => {
  // pick() returns a new object reference on every call; zustand/shallow would
  // always return false, causing Signal to re-render on every unrelated store
  // mutation and re-fire all idle-agent restart timers. Compare selected fields
  // directly so only genuine changes to id, embedded, status, or lastShutDownAt
  // trigger a re-render. Per rule: code-comments-required (ai.knowledge/global).
  const agents = useAgents(
    (µ) => µ.map((a) => pick(a, [...signalAgentPick])),
    (a, b) => fieldKeyedAgentsEqual(a, b, signalAgentPick)
  );

  const roomId = useContext(Context);
  const pending = useRef(
    new Map<Agent['id'], { timer: ReturnType<typeof setTimeout>; abort: AbortController }>()
  );

  // Reconcile per agent: a peer changing status must not cancel a healthy
  // agent's START or replenish its retry budget. Room teardown owns all entries.
  useEffect(() => {
    if (!roomId) return;
    const owned = pending.current;
    const cancel = (): void => {
      for (const entry of owned.values()) {
        clearTimeout(entry.timer);
        entry.abort.abort();
      }
      owned.clear();
      retireRoomStarts(roomId);
    };
    // Account changes can occur before a delayed idle timer fires, including
    // A→B→A before React renders again. Cancel synchronously at the store edge.
    const unsubscribe = depot.subscribe((state, previous) => {
      if (state.user?.id !== previous.user?.id) cancel();
    });
    return () => {
      unsubscribe();
      cancel();
    };
  }, [roomId]);

  useEffect(() => {
    if (!roomId) return;
    const owned = pending.current;
    for (const [id, entry] of owned) {
      const agent = agents.find((candidate) => candidate.id === id);
      if (agent?.status !== 'idle') {
        clearTimeout(entry.timer);
        if (!agent || agent.status === 'online') entry.abort.abort();
        owned.delete(id);
      }
    }
    for (const agent of agents) {
      if (!agent.id || agent.status !== 'idle' || owned.has(agent.id)) continue;
      const id = agent.id;
      const abort = new AbortController();
      const accountId = depot.getState().user?.id;
      const timer = setTimeout(() => {
        if (!abort.signal.aborted && depot.getState().user?.id === accountId) {
          void startAgent(id, roomId, abort.signal);
        }
      }, idleRestartDelayMs(agent.lastShutDownAt));
      owned.set(id, { timer, abort });
    }
  }, [agents, roomId]);

  return null;
};
